import { Alert, Button, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { listServiceLocations } from '../services/api';
import type { ServiceLocation } from '../types/incident';

type ServiceLocationWithLocation = ServiceLocation & {
  latitude: number;
  longitude: number;
};

type MapPosition = {
  lat: number;
  lng: number;
};

type GoogleMapsMap = {
  fitBounds: (bounds: GoogleMapsBounds, padding?: number) => void;
  setCenter: (position: MapPosition) => void;
  setZoom: (zoom: number) => void;
};

type GoogleMapsMarker = {
  addListener: (eventName: 'click', handler: () => void) => void;
  setMap: (map: GoogleMapsMap | null) => void;
};

type GoogleMapsBounds = {
  extend: (position: MapPosition) => void;
};

type GoogleMapsNamespace = {
  LatLngBounds: new () => GoogleMapsBounds;
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapsMap;
  Marker: new (options: { icon?: string; map: GoogleMapsMap; position: MapPosition; title: string; zIndex?: number }) => GoogleMapsMarker;
};

declare global {
  interface Window {
    google?: {
      maps?: GoogleMapsNamespace;
    };
  }
}

let googleMapsLoadPromise: Promise<void> | null = null;

const loadGoogleMaps = (apiKey: string) => {
  if (window.google?.maps) {
    return Promise.resolve();
  }
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google Maps failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.dataset.googleMaps = 'true';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Maps failed to load.'));
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

type ServiceLocationMapProps = {
  serviceLocations: ServiceLocationWithLocation[];
  onServiceLocationClick: (serviceLocation: ServiceLocation) => void;
};

const DEFAULT_MAP_CENTER = { lat: 20, lng: 0 };
const LOW_INCIDENT_COLOR = '#ffffff';
const HIGH_INCIDENT_COLOR = '#dc2626';

const interpolateMarkerColor = (incidentCount: number, maxIncidentCount: number) => {
  if (incidentCount <= 0 || maxIncidentCount <= 0) {
    return LOW_INCIDENT_COLOR;
  }
  const ratio = Math.min(1, incidentCount / maxIncidentCount);
  const start = { r: 255, g: 255, b: 255 };
  const end = { r: 220, g: 38, b: 38 };
  if (ratio === 1) {
    return HIGH_INCIDENT_COLOR;
  }
  const r = Math.round(start.r + (end.r - start.r) * ratio);
  const g = Math.round(start.g + (end.g - start.g) * ratio);
  const b = Math.round(start.b + (end.b - start.b) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
};

const markerIcon = (fillColor: string) => {
  const strokeColor = fillColor === LOW_INCIDENT_COLOR ? '#6b7280' : '#991b1b';
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
      <path d="M17 44C12.5 36.4 3 27.8 3 17.2C3 9.4 9.3 3 17 3s14 6.4 14 14.2C31 27.8 21.5 36.4 17 44Z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2"/>
      <circle cx="17" cy="17" r="5.5" fill="#ffffff" stroke="${strokeColor}" stroke-width="1.6"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const LEGEND_STEPS = [0, 0.25, 0.5, 0.75, 1];

const ServiceLocationMap = ({ serviceLocations, onServiceLocationClick }: ServiceLocationMapProps) => {
  const { t } = useTranslation();
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMapsMap | null>(null);
  const markersRef = useRef<GoogleMapsMarker[]>([]);
  const [mapStatus, setMapStatus] = useState<'ready' | 'missingKey' | 'error'>('ready');
  const [mapReady, setMapReady] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const maxIncidentCount = useMemo(
    () => Math.max(0, ...serviceLocations.map((serviceLocation) => serviceLocation.incident_count || 0)),
    [serviceLocations]
  );

  useEffect(() => {
    if (!apiKey) {
      setMapStatus('missingKey');
      return;
    }
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !mapElementRef.current || !window.google?.maps) {
          return;
        }
        if (!mapRef.current) {
          mapRef.current = new window.google.maps.Map(mapElementRef.current, {
            center: DEFAULT_MAP_CENTER,
            clickableIcons: false,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            zoom: 2,
          });
        }
        setMapStatus('ready');
        setMapReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setMapStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    const map = mapRef.current;
    const maps = window.google?.maps;
    if (!map || !maps) {
      return;
    }

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = serviceLocations.map((serviceLocation) => {
      const incidentCount = serviceLocation.incident_count || 0;
      const marker = new maps.Marker({
        icon: markerIcon(interpolateMarkerColor(incidentCount, maxIncidentCount)),
        map,
        position: { lat: serviceLocation.latitude, lng: serviceLocation.longitude },
        title: `${serviceLocation.name} (${incidentCount})`,
        zIndex: incidentCount,
      });
      marker.addListener('click', () => onServiceLocationClick(serviceLocation));
      return marker;
    });

    if (serviceLocations.length === 0) {
      map.setCenter(DEFAULT_MAP_CENTER);
      map.setZoom(2);
      return;
    }

    if (serviceLocations.length === 1) {
      map.setCenter({ lat: serviceLocations[0].latitude, lng: serviceLocations[0].longitude });
      map.setZoom(8);
      return;
    }

    const bounds = new maps.LatLngBounds();
    serviceLocations.forEach((serviceLocation) => {
      bounds.extend({ lat: serviceLocation.latitude, lng: serviceLocation.longitude });
    });
    map.fitBounds(bounds, 64);
  }, [mapReady, maxIncidentCount, onServiceLocationClick, serviceLocations]);

  return (
    <div className="factoryMapPane">
      <div ref={mapElementRef} className="factoryMapCanvas" />
      {mapStatus !== 'ready' ? (
        <div className="factoryMapState">
          <Alert
            type={mapStatus === 'missingKey' ? 'warning' : 'error'}
            message={t(mapStatus === 'missingKey' ? 'serviceLocation.map.missingKey' : 'serviceLocation.map.loadError')}
          />
        </div>
      ) : null}
      <div className="factoryMapLegend" aria-label={t('serviceLocation.map.incidentLegend')}>
        <span className="factoryMapLegendText">{t('serviceLocation.map.lessIncidents')}</span>
        <div className="factoryMapLegendScale" aria-hidden="true">
          {LEGEND_STEPS.map((step) => (
            <span
              key={step}
              className="factoryMapLegendCell"
              style={{ background: interpolateMarkerColor(Math.round(maxIncidentCount * step), maxIncidentCount) }}
            />
          ))}
        </div>
        <span className="factoryMapLegendText">{t('serviceLocation.map.moreIncidents', { count: maxIncidentCount })}</span>
      </div>
    </div>
  );
};

const ServiceLocationListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceLocations, setServiceLocations] = useState<ServiceLocation[]>([]);
  const [keyword, setKeyword] = useState(() => searchParams.get('q') || '');
  const [regionCode, setRegionCode] = useState<string | undefined>(() => searchParams.get('region') || undefined);
  const [organizationName, setOrganizationName] = useState<string | undefined>(() => searchParams.get('organization') || undefined);

  useEffect(() => {
    listServiceLocations().then(setServiceLocations).catch(() => setServiceLocations([]));
  }, []);

  useEffect(() => {
    setKeyword(searchParams.get('q') || '');
    setRegionCode(searchParams.get('region') || undefined);
    setOrganizationName(searchParams.get('organization') || undefined);
  }, [searchParams]);

  const setFilterParam = (key: string, value?: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  };

  const regionOptions = useMemo(
    () => Array.from(new Set(serviceLocations.map((location) => location.region_code).filter(Boolean))).map((value) => ({ value, label: value })),
    [serviceLocations]
  );
  const organizationOptions = useMemo(
    () => Array.from(new Set(serviceLocations.map((location) => location.organization_name).filter(Boolean))).map((value) => ({ value, label: value })),
    [serviceLocations]
  );
  const filteredServiceLocations = useMemo(
    () =>
      serviceLocations.filter((location) => {
        const matchesKeyword =
          !keyword ||
          `${location.name} ${location.code} ${location.address || ''}`.toLowerCase().includes(keyword.toLowerCase());
        const matchesRegion = !regionCode || location.region_code === regionCode;
        const matchesOrganization = !organizationName || location.organization_name === organizationName;
        return matchesKeyword && matchesRegion && matchesOrganization;
      }),
    [keyword, organizationName, regionCode, serviceLocations]
  );

  const mappedServiceLocations = useMemo(
    () =>
      filteredServiceLocations.filter(
        (serviceLocation): serviceLocation is ServiceLocationWithLocation =>
          typeof serviceLocation.latitude === 'number' && typeof serviceLocation.longitude === 'number'
      ),
    [filteredServiceLocations]
  );

  const navigateToServiceLocation = useCallback(
    (serviceLocation: ServiceLocation) => {
      navigate(`/service-locations/${serviceLocation.id}`, {
        state: { serviceLocationListPath: `${location.pathname}${location.search}` },
      });
    },
    [location.pathname, location.search, navigate]
  );

  const columns: ColumnsType<ServiceLocation> = [
    {
      title: t('serviceLocation.columns.name'),
      dataIndex: 'name',
      render: (value, record) => (
        <Button type="link" className="tableLink" onClick={() => navigateToServiceLocation(record)}>
          {value}
        </Button>
      ),
    },
    { title: t('serviceLocation.columns.organization'), dataIndex: 'organization_name', ellipsis: true },
    { title: t('serviceLocation.columns.region'), dataIndex: 'region_code', width: 110 },
  ];

  return (
    <div className="page factoryExplorerPage">
      <div className="factoryExplorerLayout">
        <ServiceLocationMap serviceLocations={mappedServiceLocations} onServiceLocationClick={navigateToServiceLocation} />
        <aside className="factoryListPane">
          <Space className="filterBar factoryFilterBar" direction="vertical">
            <Input
              allowClear
              placeholder={t('master.filters.keyword')}
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setFilterParam('q', event.target.value.trim() || null);
              }}
            />
            <Select
              allowClear
              placeholder={t('master.filters.region')}
              value={regionCode}
              onChange={(value) => {
                setRegionCode(value);
                setFilterParam('region', value || null);
              }}
              options={regionOptions}
            />
            <Select
              allowClear
              placeholder={t('master.filters.organization')}
              value={organizationName}
              onChange={(value) => {
                setOrganizationName(value);
                setFilterParam('organization', value || null);
              }}
              options={organizationOptions}
            />
          </Space>
          <div className="factoryListMeta">
            <span>{t('serviceLocation.map.visibleServiceLocations', { count: filteredServiceLocations.length })}</span>
            <Space size={6}>
              <Tag>{t('serviceLocation.map.mappedServiceLocations', { count: mappedServiceLocations.length })}</Tag>
              <Tag>{t('serviceLocation.map.maxIncidents', { count: Math.max(0, ...mappedServiceLocations.map((serviceLocation) => serviceLocation.incident_count || 0)) })}</Tag>
            </Space>
          </div>
          <Table
            rowKey="id"
            className="factoryListTable"
            columns={columns}
            dataSource={filteredServiceLocations}
            pagination={{ pageSize: 12, size: 'small' }}
            size="small"
          />
        </aside>
      </div>
    </div>
  );
};

export default ServiceLocationListPage;
